import { useEffect } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { socketEmit, socketOff, socketOn } from 'lib/clients/socket';

import { kioskHealthState, socketHasConnection, userState } from 'components/state';

const HEALTH_EVENT = 'kiosk-health-update';

/**
 * Live kiosk + terminal health for a SINGLE kiosk, independent of the
 * practice-portal sidebar monitor (useKioskHealthMonitor). Seeds the
 * `kioskHealthState` atom from the kiosk's persisted health, joins its socket
 * room via `kiosk-join` (which makes the backend request fresh telemetry), and
 * merges live `kiosk-health-update` broadcasts.
 *
 * Use this wherever a kiosk's battery is shown outside the sidebar — e.g. the
 * kiosk edit panel, which is reachable from the admin portal where the sidebar
 * monitor never mounts, so the battery would otherwise stay greyed out.
 */
export const useKioskHealthFor = (kiosk) => {
  const user = useRecoilValue(userState);
  const hasConnection = useRecoilValue(socketHasConnection);
  const setKioskHealth = useSetRecoilState(kioskHealthState);
  const kioskId = kiosk?.id;
  const token = kiosk?.token;
  const health = kiosk?.health;

  // Seed from persisted health so the battery shows immediately on open instead
  // of staying greyed-out until the first live broadcast. Existing (live) atom
  // values take precedence so a re-open never clobbers fresher socket data.
  useEffect(() => {
    if (!kioskId || !health) return;
    setKioskHealth((prev) => ({
      ...prev,
      [kioskId]: { name: kiosk?.name, ...health, ...prev[kioskId] }
    }));
  }, [kioskId, health, kiosk?.name, setKioskHealth]);

  // Join the kiosk room (backend requests fresh telemetry on join, so the
  // reading is current) and follow live health broadcasts for this kiosk.
  useEffect(() => {
    if (!kioskId || !token) return undefined;
    socketEmit('kiosk-join', { token, user_id: user?.id }, { reemitOnReconnect: true });
    const handler = (payload) => {
      if (payload?.kioskId !== kioskId) return;
      setKioskHealth((prev) => ({ ...prev, [kioskId]: { ...prev[kioskId], ...payload } }));
    };
    socketOn(HEALTH_EVENT, handler);
    return () => socketOff(HEALTH_EVENT, handler);
  }, [kioskId, token, user?.id, hasConnection, setKioskHealth]);
};
